import SecurityIcon from '@material-design-icons/svg/filled/security.svg?react';
import { isRight } from 'fp-ts/Either';
import { use, useEffect } from 'react';
import { TRPCContext } from '../contexts/TRPC';
import { useAsyncData } from '../hooks/useAsyncData';
import { AuthStateType, useAuth } from '../hooks/useAuth';
import { usePageTitle } from '../hooks/usePageTitle';
import { captureAndReportError } from '../utils/errorReporting';

interface DashboardStats {
  totalUsers: number;
  totalDapps: number;
  pendingKyb: number;
  activeInvites: number;
}

export function AdminPage() {
  const [authState] = useAuth();
  const trpc = use(TRPCContext);
  const {
    data: stats,
    isLoading: loading,
    run,
  } = useAsyncData<DashboardStats>({
    initialData: {
      totalUsers: 0,
      totalDapps: 0,
      pendingKyb: 0,
      activeInvites: 0,
    },
    initialLoading: true,
  });

  usePageTitle('Admin Dashboard | Publishing Portal');

  useEffect(() => {
    if (authState.type !== AuthStateType.Authenticated) return;

    void run(
      () =>
        Promise.all([
          trpc.client.listUsers.query({
            filters: {},
            pagination: {
              page: 1,
              limit: 1,
            },
          }),
          trpc.client.listAllDapps.query({
            filters: {},
            pagination: {
              page: 1,
              limit: 1,
            },
          }),
          trpc.client.getAllKybSubmissions.query({
            page: 1,
            limit: 1,
          }),
          trpc.client.listInviteCodes.query({
            status: 'all',
          }),
        ]),
      {
        onSuccess: (
          [usersResult, dappsResult, kybResult, invitesResult],
          helpers,
        ) => {
          helpers.setData((previousStats) => {
            const nextStats = { ...previousStats };

            if (isRight(usersResult)) {
              nextStats.totalUsers = usersResult.right.pagination.total;
            }

            if (isRight(dappsResult)) {
              nextStats.totalDapps = dappsResult.right.pagination.total;
            }

            if (isRight(kybResult)) {
              nextStats.pendingKyb = kybResult.right.submissions.filter(
                (s: { status: string }) =>
                  s.status === 'InProgress' || s.status === 'UnderReview',
              ).length;
            }

            if (isRight(invitesResult)) {
              nextStats.activeInvites = invitesResult.right.inviteCodes.filter(
                (i) => i.status === 'available',
              ).length;
            }

            return nextStats;
          });
        },
        onError: (error) => {
          captureAndReportError(error, {
            operation: 'Failed to fetch dashboard stats',
            component: 'AdminPage',
          });
        },
      },
    );
  }, [authState, run, trpc.client]);

  if (
    authState.type !== AuthStateType.Authenticated ||
    authState.user.role === 'base'
  ) {
    return null;
  }

  return (
    <div className="flex-1 overflow-y-auto p-4 md:p-8">
      <div className="max-w-7xl mx-auto space-y-8">
        <div className="space-y-2">
          <div className="bg-neutral-800/50 rounded-2xl border border-neutral-700 overflow-hidden">
            <div className="px-6 py-5 border-b border-neutral-700">
              <div className="flex items-center gap-3">
                <SecurityIcon className="fill-current text-white size-5" />
                <h3 className="text-lg font-semibold text-white">
                  Administrative Access
                </h3>
              </div>
              <p className="mt-2 text-sm text-neutral-400">
                This page is restricted to admin users only.
              </p>
            </div>

            <div className="divide-y divide-neutral-700">
              <div className="px-6 py-4 flex items-center justify-between">
                <dt className="text-sm font-medium text-neutral-400">
                  User Role
                </dt>
                <dd className="text-sm text-white bg-blue-600/20 px-3 py-1 rounded-full border border-blue-600/30">
                  admin
                </dd>
              </div>
              <div className="px-6 py-4 flex items-center justify-between">
                <dt className="text-sm font-medium text-neutral-400">Email</dt>
                <dd className="text-sm text-white">{authState.user.email}</dd>
              </div>
              <div className="px-6 py-4 flex items-center justify-between">
                <dt className="text-sm font-medium text-neutral-400">
                  Full Name
                </dt>
                <dd className="text-sm text-white">
                  {authState.user.fullName}
                </dd>
              </div>
              <div className="px-6 py-4 flex items-center justify-between">
                <dt className="text-sm font-medium text-neutral-400">
                  User ID
                </dt>
                <dd className="text-sm text-white font-mono">
                  {authState.user.id}
                </dd>
              </div>
            </div>
          </div>

          <div className="bg-neutral-800/50 rounded-2xl border border-neutral-700 overflow-hidden">
            <div className="px-6 py-5 border-b border-neutral-700">
              <h3 className="text-lg font-semibold text-white">
                Platform Overview
              </h3>
              <p className="mt-2 text-sm text-neutral-400">
                Quick statistics and platform metrics.
              </p>
            </div>

            {loading ? (
              <div className="p-6">
                <div className="text-center text-neutral-400">
                  Loading statistics…
                </div>
              </div>
            ) : (
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-neutral-900/50 rounded-lg p-4 border border-neutral-700">
                    <div className="text-2xl font-semibold text-white">
                      {stats.totalUsers}
                    </div>
                    <div className="text-sm text-neutral-400 mt-1">
                      Total Users
                    </div>
                  </div>

                  <div className="bg-neutral-900/50 rounded-lg p-4 border border-neutral-700">
                    <div className="text-2xl font-semibold text-white">
                      {stats.totalDapps}
                    </div>
                    <div className="text-sm text-neutral-400 mt-1">
                      Total Dapps
                    </div>
                  </div>

                  <div className="bg-neutral-900/50 rounded-lg p-4 border border-neutral-700">
                    <div className="text-2xl font-semibold text-white">
                      {stats.pendingKyb}
                    </div>
                    <div className="text-sm text-neutral-400 mt-1">
                      Pending KYC/KYB
                    </div>
                  </div>

                  <div className="bg-neutral-900/50 rounded-lg p-4 border border-neutral-700">
                    <div className="text-2xl font-semibold text-white">
                      {stats.activeInvites}
                    </div>
                    <div className="text-sm text-neutral-400 mt-1">
                      Active Invites
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
